import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { PuffLoader } from "react-spinners";
import { ChevronDown, ChevronLeft, ChevronRight, ExclamationTriangle, Plus } from "react-bootstrap-icons";
import { expandOccurrences } from "@api";
import { getServerErrorMessage } from "@api";
import {
  Button,
  Dropdown,
  DropdownBody,
  DropdownButton,
  DropdownItem,
  PageHeader,
  PageTitle,
} from "@components";
import { toast } from "@components/toast/Toast";
import AgendaList from "../components/AgendaList";
import CalendarLegend from "../components/CalendarLegend";
import CalendarsMenu from "../components/CalendarsMenu";
import MonthGrid from "../components/MonthGrid";
import WeekView from "../components/WeekView";
import EventDetails from "../displays/EventDetails";
import EventForm from "../flows/EventForm";
import {
  accountRootForUser,
  useCalendarItems,
  useCalendars,
  useCancelProjectedAction,
  useCreateCalendarTask,
  useCustomerAccountCodes,
  useDeleteCalendarItem,
  useSaveCalendarItem,
  useTeamCalendarItems,
} from "../hooks/useCalendar";
import { buildOwnerColorMap } from "../common";
import { useMerchant, useUser } from "@auth";
import { useUsers } from "@api";
import { useAiFeatures } from "../../../flags";
import { useCoalescedInvalidation } from "../../../realtime/useCoalescedInvalidation";
import { channels } from "../../../realtime/channels";

// Activities that change what the calendar shows: plans, promises, their payment requests, tasks.
const CALENDAR_EVENT_NAMES = [
  "ActionStarted",
  "ActionCancelled",
  "TaskCreated",
  "PaymentRequestSent",
  "PaymentRequestPaid",
  "PaymentRequestCancelled",
  "PaymentReceived",
];

const CALENDAR_QUERY_KEYS = [
  ["caldav", "objects"],
  ["caldav", "user-objects"],
];

const VIEWS = [
  { key: "month", label: "Month" },
  { key: "week", label: "Week" },
  { key: "agenda", label: "List" },
];

const AGENDA_DAYS = 30;

const rangeFor = (view, viewDate) => {
  if (view === "week") {
    const start = startOfWeek(viewDate, { weekStartsOn: 1 });
    return [start, addDays(start, 7)];
  }
  if (view === "agenda") {
    const start = new Date(viewDate);
    start.setHours(0, 0, 0, 0);
    return [start, addDays(start, AGENDA_DAYS)];
  }
  return [
    startOfWeek(startOfMonth(viewDate), { weekStartsOn: 1 }),
    addDays(endOfWeek(endOfMonth(viewDate), { weekStartsOn: 1 }), 1),
  ];
};

const titleFor = (view, viewDate) => {
  if (view === "week") {
    const start = startOfWeek(viewDate, { weekStartsOn: 1 });
    const end = addDays(start, 6);
    return start.getMonth() === end.getMonth()
      ? `${format(start, "d")} – ${format(end, "d MMMM yyyy")}`
      : `${format(start, "d MMM")} – ${format(end, "d MMM yyyy")}`;
  }
  if (view === "agenda") return `Next ${AGENDA_DAYS} days`;
  return format(viewDate, "MMMM yyyy");
};

const Calendar = () => {
  const [view, setView] = useState("month");
  const [viewDate, setViewDate] = useState(new Date());
  const [weekdaysOnly, setWeekdaysOnly] = useState(() => {
    if (typeof localStorage === "undefined") return true;
    const stored = localStorage.getItem("calendar-weekdays-only");
    return stored === null ? true : stored === "true";
  });
  const toggleWeekdays = () =>
    setWeekdaysOnly((current) => {
      localStorage.setItem("calendar-weekdays-only", String(!current));
      return !current;
    });
  const [selectedItem, setSelectedItem] = useState(null);
  const [formState, setFormState] = useState(null); // { item } | { defaultDate }
  const { itemId } = useParams();
  const navigateTo = useNavigate();

  const queryClient = useQueryClient();
  const { merchant } = useMerchant();
  const { user } = useUser();
  const aiEnabled = useAiFeatures();
  const meId = user?.guid ?? null;

  // Own calendar — always shown, and the only one that stays editable.
  const { data: calendars, isLoading: calendarsLoading, error: calendarsError } = useCalendars();
  const calendar = calendars?.[0];

  const {
    data: ownItems = [],
    isLoading: itemsLoading,
    error: itemsError,
  } = useCalendarItems(calendar?.href);

  const saveItem = useSaveCalendarItem(calendar?.href);
  const createTask = useCreateCalendarTask(calendar?.href);
  const deleteItem = useDeleteCalendarItem(calendar?.href);
  const cancelAction = useCancelProjectedAction(calendar?.href);

  // Overlaid colleagues (the shared "team view").
  const { data: users = [] } = useUsers();
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // Restore the overlay selection per merchant; reset when switching merchant.
  useEffect(() => {
    if (!merchant?.id) return;
    try {
      const stored = JSON.parse(localStorage.getItem(`calendar-team:${merchant.id}`) ?? "[]");
      setSelectedUserIds(Array.isArray(stored) ? stored : []);
    } catch {
      setSelectedUserIds([]);
    }
  }, [merchant?.id]);

  const persistTeam = (next) => {
    if (merchant?.id) {
      localStorage.setItem(`calendar-team:${merchant.id}`, JSON.stringify(next));
    }
    return next;
  };

  const toggleUser = (userId) => {
    setSelectedUserIds((current) =>
      persistTeam(
        current.includes(userId)
          ? current.filter((id) => id !== userId)
          : [...current, userId],
      ),
    );
  };

  const clearTeam = () => setSelectedUserIds(persistTeam([]));

  const overlayActive = aiEnabled && selectedUserIds.length > 0;

  // One colour per owner, assigned by position across a stable roster (me first, then colleagues by
  // name) so every calendar on screen is a distinct colour and the picker swatch matches the grid.
  const colorByUser = useMemo(() => {
    const colleagueIds = users
      .filter((u) => u.id !== meId)
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
      .map((u) => u.id);
    return buildOwnerColorMap([meId, ...colleagueIds]);
  }, [users, meId]);

  // Owner name per user id, for the legend and the chip tooltip / detail attribution. Your own
  // resolves to your name; support users still resolve so their items are labelled.
  const ownerNameById = useMemo(() => {
    const map = {};
    if (meId != null) map[meId] = user?.name ?? "You";
    users.forEach((u) => {
      map[u.id] = u.name || u.email || "Unknown user";
    });
    return map;
  }, [users, meId, user]);

  const sources = useMemo(
    () =>
      overlayActive
        ? selectedUserIds.map((userId) => ({ userId, accountRoot: accountRootForUser(userId) }))
        : [],
    [overlayActive, selectedUserIds],
  );
  const teamResults = useTeamCalendarItems(sources, meId);

  // Per-source status for the picker (loading / error dots), keyed by user id.
  const sourceStatus = useMemo(() => {
    const status = {};
    sources.forEach((source, index) => {
      const result = teamResults[index];
      status[source.userId] = { isLoading: result?.isLoading, isError: result?.isError };
    });
    return status;
  }, [sources, teamResults]);

  // Merge own + colleague items. When overlaying, stripe every item with its owner's colour
  // (an additive accent — the STATUS/projection palette stays intact).
  const items = useMemo(() => {
    const own = ownItems.map((item) => ({ ...item, ownerUserId: meId, readOnly: false }));
    const colleague = teamResults.flatMap((result) => result.data ?? []);
    const merged = [...own, ...colleague];
    if (!overlayActive) return merged;
    return merged.map((item) => ({
      ...item,
      ownerColorClass: colorByUser[item.ownerUserId]?.borderClass,
      ownerLabel: ownerNameById[item.ownerUserId] ?? null,
    }));
  }, [ownItems, teamResults, overlayActive, meId, colorByUser, ownerNameById]);

  // Plans, promises and requests are created elsewhere; without this the calendar would wait for its
  // 60s poll. The follow-up covers the projector materialising the item a moment after the hint.
  useCoalescedInvalidation(
    merchant?.id ? channels.merchantActivity(merchant.id) : null,
    CALENDAR_QUERY_KEYS,
    { eventNames: CALENDAR_EVENT_NAMES, followUp: true },
  );

  // Deep links from ICS feeds / other services: /calendar/items/{id}, where the
  // item's iCalendar UID is "{id}@ontik.com". Auto-open the item ONCE — tracked by a ref so a later
  // items refetch (which gives `items` a new reference) can't re-run this and reopen the modal the
  // user just closed.
  const deepLinkedItemId = useRef(null);
  useEffect(() => {
    if (!itemId || !items.length || deepLinkedItemId.current === itemId) return;
    const match = items.find((item) => item.uid?.startsWith(itemId));
    if (match) {
      deepLinkedItemId.current = itemId;
      setSelectedItem(match);
      const anchor = match.start ?? match.due;
      if (anchor) setViewDate(anchor);
    }
  }, [itemId, items]);

  const closeDetails = () => {
    setSelectedItem(null);
    if (itemId) navigateTo("/calendar");
  };

  const occurrences = useMemo(() => {
    const [rangeStart, rangeEnd] = rangeFor(view, viewDate);
    return items.flatMap((item) => {
      if (item.start) return expandOccurrences(item, rangeStart, rangeEnd);
      if (item.due && item.due >= rangeStart && item.due < rangeEnd) {
        return [{ ...item, occurrenceKey: item.uid }];
      }
      return [];
    });
  }, [items, view, viewDate]);

  // Projections carry their reference on the feed; everything else (tasks/events) only carries the
  // linked customer id, so resolve those to account codes up front and hold the loading state until
  // they're all in — otherwise the codes pop in after the grid has already painted.
  const customerIdsToResolve = useMemo(
    () => occurrences.filter((o) => !o.projection?.reference).map((o) => o.customerId),
    [occurrences],
  );
  const { map: accountCodeByCustomer, isLoading: codesLoading } =
    useCustomerAccountCodes(customerIdsToResolve);
  const decoratedOccurrences = useMemo(
    () =>
      occurrences.map((o) => ({
        ...o,
        accountCode: o.projection?.reference ?? accountCodeByCustomer[o.customerId] ?? null,
      })),
    [occurrences, accountCodeByCustomer],
  );

  // Legend for the overlay: you first, then each selected colleague, with their colour, per-source
  // status, and how many of their events fall in the current view (a "0" flags a genuinely empty
  // colleague rather than a broken load).
  const legendEntries = useMemo(() => {
    if (!overlayActive) return [];
    const counts = {};
    occurrences.forEach((o) => {
      counts[o.ownerUserId] = (counts[o.ownerUserId] ?? 0) + 1;
    });
    const entry = (id, isYou) => ({
      userId: id,
      isYou,
      name: ownerNameById[id] ?? "Unknown user",
      swatchClass: colorByUser[id]?.swatchClass,
      count: counts[id] ?? 0,
      isLoading: !isYou && !!sourceStatus[id]?.isLoading,
      isError: !isYou && !!sourceStatus[id]?.isError,
    });
    return [entry(meId, true), ...selectedUserIds.map((id) => entry(id, false))];
  }, [overlayActive, occurrences, ownerNameById, colorByUser, sourceStatus, meId, selectedUserIds]);

  const navigate = (direction) => {
    setViewDate((current) => {
      if (view === "week") return addWeeks(current, direction);
      if (view === "agenda") return addDays(current, direction * AGENDA_DAYS);
      return addMonths(current, direction);
    });
  };

  const handleSave = (item) => {
    saveItem.mutate(
      { item },
      {
        onSuccess: () => {
          setFormState(null);
          setSelectedItem(null);
          toast({ description: item.uid ? "Calendar item updated" : "Calendar item created" });
        },
        onError: (error) =>
          toast({
            description:
              error?.response?.status === 412
                ? "This item changed elsewhere — refresh and try again"
                : "Couldn't save the calendar item",
          }),
      },
    );
  };

  const handleCreate = (task) => {
    createTask.mutate(task, {
      onSuccess: () => {
        setFormState(null);
        const assigneeName = task.assigneeUserId && task.assigneeUserId !== meId
          ? ownerNameById[task.assigneeUserId]
          : null;
        toast({ description: assigneeName ? `Task assigned to ${assigneeName}` : "Task created" });
      },
      onError: (error) =>
        toast({ description: getServerErrorMessage(error) || "Couldn't create the task" }),
    });
  };

  const handleDelete = () => {
    deleteItem.mutate(
      { item: selectedItem },
      {
        onSuccess: () => {
          closeDetails();
          toast({ description: "Calendar item deleted" });
        },
        onError: () => toast({ description: "Couldn't delete the calendar item" }),
      },
    );
  };

  // Projected payment-request items aren't deleted — the action behind them is cancelled on
  // the action-service. The clicked item is dropped from the cached feed immediately; a plan's
  // sibling instalments disappear once the projector's cancellation lands (next poll).
  const handleCancelAction = (actionId) => {
    cancelAction.mutate(
      { actionId, item: selectedItem },
      {
        onSuccess: () => {
          closeDetails();
          toast({ description: "Scheduled work cancelled" });
        },
        onError: (error) =>
          toast({
            description:
              getServerErrorMessage(error) || "Couldn't cancel the scheduled work",
          }),
      },
    );
  };

  const loading = calendarsLoading || (calendar && itemsLoading) || codesLoading;
  const error = calendarsError || itemsError;

  const selectedReadOnly = !!selectedItem?.readOnly;

  // Who a calendar item belongs to, for the detail modal. Only surfaced while overlaying (on a solo
  // own-calendar view it's just noise); resolves your own name too so every item is attributable.
  const selectedOwner = useMemo(() => {
    if (!selectedItem || selectedItem.ownerUserId == null) return null;
    if (!overlayActive && !selectedReadOnly) return null;
    const isYou = selectedItem.ownerUserId === meId;
    const name = isYou
      ? user?.name ?? "You"
      : users.find((u) => u.id === selectedItem.ownerUserId)?.name ?? "Unknown user";
    return {
      name,
      isYou,
      swatchClass: colorByUser[selectedItem.ownerUserId]?.swatchClass,
    };
  }, [selectedItem, overlayActive, selectedReadOnly, meId, user, users, colorByUser]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader>
        <PageTitle>Calendar</PageTitle>
        <span className="text-sm font-medium text-zinc-500">{titleFor(view, viewDate)}</span>
      </PageHeader>

      {/* Toolbar: shared-calendar avatars on the left, the view/create controls on the right. Lifted
          off the header so the live Collected figure can sit there like it does on every page. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-zinc-200 px-4 py-2">
        {overlayActive && <CalendarLegend entries={legendEntries} onRemove={toggleUser} />}

        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            <Button color="text" size="sm" onClick={() => navigate(-1)} aria-label="Previous">
              <ChevronLeft className="size-4" />
            </Button>
            <Button color="secondary" size="sm" onClick={() => setViewDate(new Date())}>
              Today
            </Button>
            <Button color="text" size="sm" onClick={() => navigate(1)} aria-label="Next">
              <ChevronRight className="size-4" />
            </Button>
          </div>

          <Dropdown>
            <DropdownButton className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:bg-zinc-50">
              {VIEWS.find((v) => v.key === view)?.label}
              {view !== "agenda" && weekdaysOnly && " · Exc. Weekends"}
              <ChevronDown className="size-3.5 text-zinc-500" />
            </DropdownButton>
            <DropdownBody position="left" className="w-56">
              {VIEWS.map(({ key, label }) => (
                <DropdownItem
                  key={key}
                  closeOnComplete
                  onClick={() => setView(key)}
                  className={view === key ? "bg-zinc-50 font-medium text-zinc-900" : ""}
                >
                  {label}
                </DropdownItem>
              ))}
              {view !== "agenda" && (
                <>
                  <div className="my-1 border-t border-zinc-100" />
                  <DropdownItem onClick={toggleWeekdays} className="gap-2.5 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={weekdaysOnly}
                      readOnly
                      className="pointer-events-none size-4 shrink-0 rounded border-zinc-300 accent-primary"
                    />
                    Exclude weekends
                  </DropdownItem>
                </>
              )}
            </DropdownBody>
          </Dropdown>

          <CalendarsMenu
            me={{ id: meId, name: user?.name }}
            users={users}
            selectedIds={selectedUserIds}
            onToggle={toggleUser}
            onClear={clearTeam}
            sourceStatus={sourceStatus}
            colorByUser={colorByUser}
            showTeam={aiEnabled}
          />

          <Button color="primary" onClick={() => setFormState({ defaultDate: null })}>
            <Plus className="mr-1 size-4" />
            New task
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 p-4">
        {error ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white text-zinc-500 shadow-sm">
            <ExclamationTriangle className="size-8 text-yellow-500" />
            <p className="text-sm font-medium text-zinc-700">Couldn't load your calendar</p>
            <p className="text-sm">Check the calendar service is reachable and try again.</p>
          </div>
        ) : loading ? (
          <div className="flex h-full items-center justify-center">
            <PuffLoader color="#F95816" size={64} />
          </div>
        ) : view === "week" ? (
          <WeekView
            viewDate={viewDate}
            items={decoratedOccurrences}
            weekdaysOnly={weekdaysOnly}
            onSelectItem={setSelectedItem}
            onCreateAt={(at) => setFormState({ defaultDate: at })}
          />
        ) : view === "agenda" ? (
          <AgendaList items={decoratedOccurrences} onSelectItem={setSelectedItem} />
        ) : (
          <MonthGrid
            viewDate={viewDate}
            items={decoratedOccurrences}
            weekdaysOnly={weekdaysOnly}
            onSelectItem={setSelectedItem}
            onCreateAt={(day) => {
              const at = new Date(day);
              at.setHours(9, 0, 0, 0);
              setFormState({ defaultDate: at });
            }}
          />
        )}
      </div>

      <EventDetails
        item={selectedItem}
        isOpen={!!selectedItem && !formState}
        onClose={closeDetails}
        readOnly={selectedReadOnly}
        owner={selectedOwner}
        onEdit={(realDescription) =>
          setFormState({
            // Seed from the REAL description (from the detail modal), not the feed's redacted
            // placeholder — otherwise editing shows/persists the wrong description.
            item: { ...selectedItem, description: realDescription ?? selectedItem?.description ?? "" },
          })
        }
        onDelete={handleDelete}
        deleting={deleteItem.isPending}
        onCancelAction={handleCancelAction}
        cancelling={cancelAction.isPending}
      />

      <EventForm
        isOpen={!!formState}
        onClose={() => setFormState(null)}
        onSave={handleSave}
        onCreate={handleCreate}
        saving={saveItem.isPending || createTask.isPending}
        item={formState?.item ?? null}
        defaultDate={formState?.defaultDate ?? null}
      />
    </div>
  );
};

export default Calendar;
